import * as yup from 'yup';
import { useState } from 'react';
import {
    getBackOfficeDeliveryIcon,
    useLazyCreateCargasBackOfficeDeliveryQuery,
} from 'store/services/third-party-backoffice';
import {
    BackOfficeDeliveryIconConfig,
    CreateCargasDeliveryFormFields,
    Device,
} from 'types';
import { DefaultOpenModalProps } from 'components/button/OpenModalButton/OpenModalButton';
import { activateSnackbar } from 'store/features/ui';
import { useDispatch } from 'react-redux';
import { FormModal } from 'components/form/FormModal/FormModal';
import { FormDatePicker, FormInputGroup } from 'components/form';
import { format } from 'date-fns';
import { Typography } from '@mui/material';
import { useLazyPatchUpdateDeviceQuery } from 'store/services';

export const createDeliverySchema = yup.object<{
    [k in keyof CreateCargasDeliveryFormFields]: yup.AnySchema;
}>({
    deliveryDate: yup.date().required(),
});

export interface BackofficeDeliveryModalProps extends DefaultOpenModalProps {
    supplierId: string;
    tankNumber: string;
    device: Device;
    /** Callback to alert parent component about device updates. */
    onDeviceUpdate: React.Dispatch<React.SetStateAction<Device | undefined>>;
}

/** Add deliveries in Cargas third-party backoffice. */
export const BackofficeDeliveryModal = ({
    supplierId,
    tankNumber,
    device,
    onDeviceUpdate,
    modalOpen,
    setModalOpen,
}: BackofficeDeliveryModalProps) => {
    const dispatch = useDispatch();

    const [createDeliveryQuery] = useLazyCreateCargasBackOfficeDeliveryQuery();
    const [updateDeviceQuery] = useLazyPatchUpdateDeviceQuery();

    const [ticketMessage, setTicketMessage] = useState('');
    const [isPending, setIsPending] = useState(false);
    const [isDateDisabled, setIsDateDisabled] = useState(false);

    const defaultCreateDeliveryValues: CreateCargasDeliveryFormFields = {
        deliveryDate: new Date(),
    };

    const createDelivery = (data: CreateCargasDeliveryFormFields) => {
        setIsPending(true);
        setIsDateDisabled(true);

        // Format delivery date into long localized date format.
        const deliveryDate = format(data.deliveryDate, 'P');

        let lastReadingDate;
        if (device.lastReading?.time) {
            // Format to 04/24 (for April 24th) format.
            lastReadingDate = format(device.lastReading.time, 'MM/dd');
        }

        if (device.lastReading?.tank) {
            // Space after % is intentional.
            setTicketMessage(
                `TU@${Math.trunc(device.lastReading?.tank)}% ${lastReadingDate}`
            );
        }

        createDeliveryQuery({
            supplierId,
            tankNumber,
            deliveryDate,
            ticketMessage,
        })
            .then(() => {
                setIsPending(false);
                const deviceUpdates: Partial<Device> = {
                    _has_delivery: true,
                };

                // Get updated delivery icon now that `_has_delivery` is `true`.
                const newIconConfig: BackOfficeDeliveryIconConfig =
                    getBackOfficeDeliveryIcon({ ...device, ...deviceUpdates });

                // Save backoffice delivery icon config updates.
                deviceUpdates._delivery_icon = newIconConfig.delivery_icon;
                deviceUpdates._delivery_style = newIconConfig.delivery_style;
                deviceUpdates._bo_delivery_msg = newIconConfig.bo_delivery_msg;

                // Make query to update this device with these new values.
                updateDeviceQuery({
                    deviceId: device.deviceId,
                    updatedFields: deviceUpdates,
                }).then((result) => {
                    if (result.isSuccess && result.data) {
                        // Alert parent component that the device has changed.
                        onDeviceUpdate(result.data);
                    }
                });
            })
            .catch(() => {
                onError();
            });
    };

    const onError = () => {
        setIsPending(false);
        dispatch(
            activateSnackbar({
                message:
                    'An error occurred while creating the delivery. Let our support team know and we will look into it',
                severity: 'error',
            })
        );
    };

    return (
        <FormModal
            validationSchema={createDeliverySchema}
            defaultValues={defaultCreateDeliveryValues}
            title="Delivery"
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            onValidHandler={createDelivery}
            onErrorHandler={onError}
            testId="BackofficeDeliveryModal"
            maxWidth="xs"
            successMessage="Delivery created successfully!"
            isLoading={isPending}
        >
            <FormInputGroup>
                <Typography variant="h6">
                    Select a date. We will schedule a delivery in CarGas.
                </Typography>
                <FormDatePicker
                    disabled={isDateDisabled}
                    field="deliveryDate"
                    label="Delivery Date"
                />
            </FormInputGroup>
        </FormModal>
    );
};
